import { ApiError } from '@/utils/apiClient';

/**
 * One quiet retry for a chat send that was turned away before any turn began.
 *
 * A send can fail before the backend streams a single frame: the server's API
 * proxy or a gateway answers 502 or 503, or a rate limit answers 429. The
 * answer bubble used to say "Let me try that again for you!" while nothing
 * retried. A non-2xx status means no turn started — once the backend streams,
 * the status is already 200 — so for the statuses that also mean the request
 * was turned away rather than half-handled, the send is repeated once, after a
 * short wait.
 *
 * 500 and 504 stay out: the backend may have begun the turn behind them, and a
 * repeat would ask the question twice. A dropped connection stays out for the
 * same reason. The mobile app's `services/chatSendRetry.ts` is the same policy.
 */

/** HTTP statuses that turn a request away before any turn starts. */
export const RETRYABLE_STATUSES: ReadonlySet<number> = new Set([408, 429, 502, 503]);
/** Wait before the retry when the response names none. */
export const RETRY_DELAY_MS = 1_500;
/** Longest `Retry-After` honored; a longer one is shown as the error it is. */
export const MAX_RETRY_WAIT_MS = 5_000;

/** Milliseconds to wait before retrying *error*, or `null` when it is not retried. */
export function retryDelayMs(error: unknown): number | null {
  if (!(error instanceof ApiError) || !RETRYABLE_STATUSES.has(error.status)) return null;
  if (!error.retryAfter) return RETRY_DELAY_MS;
  const seconds = Number(error.retryAfter);
  const wait = Number.isNaN(seconds) ? Date.parse(error.retryAfter) - Date.now() : seconds * 1000;
  if (Number.isNaN(wait)) return RETRY_DELAY_MS;
  return wait > MAX_RETRY_WAIT_MS ? null : Math.max(wait, 0);
}

function abortError(): Error {
  const error = new Error('Request aborted');
  error.name = 'AbortError';
  return error;
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Run *send*, and run it once more when it fails in a way {@link retryDelayMs}
 * retries.
 *
 * The retry goes out only while *isCurrent* still holds. A Stop pressed before
 * any `start` frame aborts the request late — it waits to learn the turn's id —
 * so the abort signal alone would let the retry send a question the voter just
 * stopped. Either way the wait ends with an `AbortError`, which settles the
 * turn as cancelled.
 */
export async function sendWithRetry<T>(
  send: () => Promise<T>,
  options: {
    signal?: AbortSignal;
    isCurrent?: () => boolean;
    onRetry?: (error: ApiError, delayMs: number) => void;
  } = {}
): Promise<T> {
  const isCurrent = options.isCurrent ?? (() => !options.signal?.aborted);
  try {
    return await send();
  } catch (error) {
    const delay = retryDelayMs(error);
    if (delay === null || !isCurrent()) throw error;
    options.onRetry?.(error as ApiError, delay);
    await wait(delay, options.signal);
    if (!isCurrent()) throw abortError();
    return send();
  }
}
